// Deterministic randomness.
//
// The whole build is judged by a blind A/B loop: capture the same four camera stations every round,
// change ONE thing, ask a critic what moved. That only works if everything I did not touch is
// byte-identical between rounds.
//
// So: no shared random stream. Every procedural decision draws from a generator keyed by a stable
// STRING ('facade.west.3.windows'). Adding a new feature mints a new key and cannot perturb the
// draws of any existing one. With a single sequential stream, inserting one extra call in round 4
// shifts every subsequent draw and the entire city reshuffles -- and then the critic's next tell is
// unattributable, because the fix and the reshuffle are confounded.

export const WORLD_SEED = 0x4e50; // 'NP'

// FNV-1a. Cheap, well-distributed for short ASCII keys.
export function hashKey(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

// mulberry32 -- small, fast, passes gjrand for our purposes.
function mulberry32(a) {
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * A generator bound to a stable key. Same key + same seed => same sequence, forever, regardless of
 * what else the build does.
 */
export function rngFor(key, seed = WORLD_SEED) {
  const r = mulberry32(hashKey(key) ^ seed);
  // Discard a few -- mulberry32's first output is weakly correlated with the seed for adjacent seeds,
  // and adjacent keys are exactly what we generate ('facade.west.3', 'facade.west.4').
  r(); r(); r();
  return {
    f: r,
    range: (lo, hi) => lo + r() * (hi - lo),
    int: (lo, hi) => Math.floor(lo + r() * (hi - lo + 1)),
    pick: (arr) => arr[Math.floor(r() * arr.length)],
    chance: (p) => r() < p,
    // Symmetric jitter around 0.
    jitter: (amt) => (r() * 2 - 1) * amt,
    // Weighted pick. weights need not sum to 1.
    weighted: (arr, weights) => {
      let total = 0;
      for (const w of weights) total += w;
      let x = r() * total;
      for (let i = 0; i < arr.length; i++) {
        x -= weights[i];
        if (x <= 0) return arr[i];
      }
      return arr[arr.length - 1];
    },
  };
}

/** One-shot deterministic float in [0,1) for a key. Handy for "should this exist?" decisions. */
export function hashFloat(key, seed = WORLD_SEED) {
  return rngFor(key, seed).f();
}

// ---------------------------------------------------------------------------
// Value noise. Used by geometry (surface sag, wear placement) and later by the
// procedural texture bakes, so it lives here rather than in the material module.
// ---------------------------------------------------------------------------

function hash2(x, y, seed) {
  let h = seed ^ Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(y | 0, 0x165667b1);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39);
  return ((h ^ (h >>> 15)) >>> 0) / 4294967296;
}

const smooth = (t) => t * t * (3 - 2 * t);

export function valueNoise2(x, y, seed = WORLD_SEED) {
  const xi = Math.floor(x), yi = Math.floor(y);
  const xf = x - xi, yf = y - yi;
  const u = smooth(xf), v = smooth(yf);
  const a = hash2(xi, yi, seed);
  const b = hash2(xi + 1, yi, seed);
  const c = hash2(xi, yi + 1, seed);
  const d = hash2(xi + 1, yi + 1, seed);
  return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
}

export function fbm2(x, y, octaves = 4, lacunarity = 2.0, gain = 0.5, seed = WORLD_SEED) {
  let amp = 1, freq = 1, sum = 0, norm = 0;
  for (let i = 0; i < octaves; i++) {
    sum += amp * valueNoise2(x * freq, y * freq, seed + i * 1013);
    norm += amp;
    amp *= gain;
    freq *= lacunarity;
  }
  return sum / norm;
}
